import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { ProfileDetailsEditPage } from 'enrollment/features/Profile/ProfileDetailsEditPage/ProfileDetailsEditPage';
import { InternalPage, Section } from 'theSource/components';
import { getReadonlyField } from 'theSource/utils';
import { FormFieldsCategory } from 'theSource/types';
import {
  ProfilePictureStyled,
  SectionBoxStyled,
  SectionStackStyled,
  StyledTypography,
} from './ProfileDetailsPageStyled';
import { EnrollmentCard } from 'enrollment/features/Shared/EnrollmentCard';
import { useEnrollmentProfileQuery } from 'enrollment/queries';
import { ROUTES } from 'enrollment/app/routes';
import { displayDependentAvatar } from 'enrollment/features/Family/FamilyDetailsPage/components';
import './ProfileDetailsPage.css';
import { PsButtonTuner } from 'stencilSetup';
import { OrbitDrawer } from 'enrollment/components/Shared/OrbitDrawer';
import { LoaderOverlay, PageContentHeaderDisplay } from 'enrollment/components';
import { selectEnrollmentOrgPlanId } from 'theSource/features/Home/components/EnrollmentBanner/services';
import { ThemedHeader } from 'components/ThemedHeader';
import { profileformFields } from './ProfileHelper';

export interface ProfileDetailsPageProps {
  categories: FormFieldsCategory[];
}

export const ProfileDetailsPage = ({ categories }: ProfileDetailsPageProps) => {
  const { t } = useTranslation('pages.enrollmentProfile');
  const { subscriber } = useEnrollmentProfileQuery({ lumosEnabled: true });
  const orgPlanYearId = useSelector(selectEnrollmentOrgPlanId);
  const [awaitSuccess, setAwaitSuccess] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const subscriberName =
    (subscriber?.data.first_name.value as string) +
    ' ' +
    (subscriber?.data.last_name.value as string);
  const subscriberProfile =
    subscriber?.data?.subscriber_profile?.profile_picture ?? '';
  const chunk1Category = categories.slice(0, categories.length / 2);
  const chunk2Category = categories.slice(
    categories.length / 2,
    categories.length
  );

  const navToPath = () => {
    navigate(`/enrollment/${ROUTES.enrollment.profileEdit.path}`);
  };

  const [isFormValid, setIsFormValid] = useState<boolean | undefined>();

  const toggleIsFormValid = (value: boolean) => {
    setIsFormValid(value);
  };

  const childRef = useRef<any>();

  const handleSubmitEditInfo = () => {
    setIsSubmitting(true);
    if (childRef.current) {
      childRef.current.handleSubmit();
    }
  };

  const renderProfileColumn = (column: any, isFirstColumn = false) => {
    return (
      <Stack direction="column" className={isFirstColumn ? 'first-column' : ''}>
        {column.map((category: any, index: number) => {
          const filteredFields = category.fields.filter(
            (field: any) => field.fieldName && Object.prototype.hasOwnProperty.call(profileformFields, field.fieldName)
          );

          return (
            <SectionBoxStyled key={index}>
              <Section
                data-testid="category"
                key={category.name}
                name={category.name}
              >
                {filteredFields.map((field: any, fieldIndex: number) => (
                  <div key={fieldIndex}>{getReadonlyField(field)}</div>
                ))
                }
              </Section>
            </SectionBoxStyled>
          );
        })}
      </Stack>
    );
  };

  const altText = t('photo') + ' ' + subscriberName;
  return (
    <InternalPage
      testId="enrollment-profile-details-page"
      id="enrollment-profile-container"
      blockClasses="mt-lg-4_5 mt-md-5 px-4 pt-3 pb-4 pt-md-3_5 p-md-0 pt-lg-0"
    >
      <ThemedHeader>{t('title')}</ThemedHeader>
      <PageContentHeaderDisplay
        pageContentLookupCode="enroll_personal_view"
        displayPageHeader={false}
        orgPlanYearId={orgPlanYearId}
      />

      <EnrollmentCard navigateTo="family" textAlign="left">
        <Stack
          sx={{
            minWidth: '260px',
            maxWidth: '260px',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          <ProfilePictureStyled>
            {subscriberProfile ? (
              <img
                src={subscriberProfile}
                alt={altText}
                style={{ width: '120px', borderRadius: '60px' }}
              />
            ) : (
              displayDependentAvatar(subscriber?.data?.subscriber_profile)
            )}

            <StyledTypography
              variant="body1"
              className="mt-3"
              id="subscriber-name"
            >
              {subscriberName}
            </StyledTypography>
            <PsButtonTuner
              buttonId="profile-avatar-edit-button"
              data-testid="profile-avatar-edit-button"
              label={t('editAvatarButtonLabel')}
              level="tertiary"
              icon="pencil-simple"
              onClick={navToPath}
              otherData={[
                {
                  attribute: 'aria-describedby',
                  value: 'subscriber-name',
                },
              ]}
            />
          </ProfilePictureStyled>
        </Stack>
        <Stack>
          <SectionStackStyled>
            <Stack direction="column">
              <Stack
                sx={{
                  px: 3,
                  position: 'relative',
                }}
              >
                <div className="edit-container pr-3_5">
                  <PsButtonTuner
                    buttonId="edit-profile"
                    level="secondary"
                    label={t('editInfoButtonLabel')}
                    icon="pencil-simple"
                    extraClass="mx-3_5"
                    onClick={() => {
                      setDrawerOpen(true);
                    }}
                    otherData={[
                      {
                        attribute: 'aria-describedby',
                        value: 'subscriber-name',
                      },
                    ]}
                  />
                </div>
                <OrbitDrawer
                  drawerId="profile_edit_info"
                  heading={t('drawerTitle')}
                  isOpen={drawerOpen}
                  handleClose={handleDrawerClose}
                  handleSubmit={handleSubmitEditInfo}
                  awaitSuccess={awaitSuccess}
                  footer={true}
                  saveButton={t('common:save')}
                  saveButtonDisabled={!isFormValid}
                >
                  <ProfileDetailsEditPage
                    categories={categories!}
                    setDrawerOpen={setDrawerOpen}
                    toggleIsFormValid={toggleIsFormValid}
                    ref={childRef}
                    setIsSubmitting={setIsSubmitting}
                    setAwaitSuccess={setAwaitSuccess}
                  />
                  <LoaderOverlay isAbsolute={true} isOverlay={true} isSubmitting={isSubmitting} />
                </OrbitDrawer>
              </Stack>
              <Stack
                sx={{
                  flexDirection: { lg: 'row', md: 'col', sm: 'col' },
                  width: { lg: '100%', sm: '100%' },
                }}
              >
                {renderProfileColumn(chunk1Category, true)}

                {renderProfileColumn(chunk2Category)}
              </Stack>
            </Stack>
          </SectionStackStyled>
        </Stack>
      </EnrollmentCard>
    </InternalPage>
  );
};
